import { z } from 'zod';

// These string unions mirror the Prisma enums exactly. Keep them in step.

export const PARTY_TYPES = [
  'MANUFACTURER',
  'DISTRIBUTOR',
  'COMPANY_OUTLET',
  'DEALER',
  'SUB_DEALER',
  'DIRECT_CUSTOMER',
  'WAREHOUSE',
] as const;
export type PartyType = (typeof PARTY_TYPES)[number];

export const VEHICLE_STATUSES = [
  'PENDING_ALLOCATION',
  'IN_BRIGHT_STOCK',
  'AT_COMPANY_OUTLET',
  'AT_DEALER',
  'AT_SUB_DEALER',
  'SOLD',
  'RETURNED',
  'CANCELLED',
] as const;
export type VehicleStatus = (typeof VEHICLE_STATUSES)[number];

export const MOVEMENT_REASONS = [
  'PURCHASE',
  'ALLOCATION',
  'DISPATCH',
  'TRANSFER',
  'SALE',
  'RETURN',
  'CORRECTION',
] as const;
export type MovementReason = (typeof MOVEMENT_REASONS)[number];

export const SOURCE_SYSTEMS = ['GOOGLE_SHEET', 'MANUAL', 'IMPORT', 'SYSTEM'] as const;
export type SourceSystem = (typeof SOURCE_SYSTEMS)[number];

export const SYNC_STATUSES = ['RUNNING', 'SUCCESS', 'PARTIAL', 'FAILED'] as const;
export type SyncStatus = (typeof SYNC_STATUSES)[number];

export const ALERT_KINDS = [
  'AGEING_OVER_60',
  'DUPLICATE_CHASSIS',
  'DUPLICATE_VIN',
  'MISSING_BUYER',
  'MISSING_INVOICE',
  'PENDING_ALLOCATION',
  'INVALID_RECORD',
  'SYNC_FAILED',
] as const;
export type AlertKind = (typeof ALERT_KINDS)[number];

export const ALERT_SEVERITIES = ['INFO', 'WARNING', 'CRITICAL'] as const;
export type AlertSeverity = (typeof ALERT_SEVERITIES)[number];

export const ALERT_STATUSES = ['OPEN', 'ACKNOWLEDGED', 'RESOLVED', 'MUTED'] as const;
export type AlertStatus = (typeof ALERT_STATUSES)[number];

export const ROLE_KEYS = [
  'ADMINISTRATOR',
  'INVENTORY_MANAGER',
  'SALES_MANAGER',
  'ASM',
  'VIEWER',
  // A buyer portal account (e.g. FAB EV). Always paired with a scopeParty, so it
  // only ever sees vehicles that buyer was sold. Read-only, no network, no sync.
  'BUYER',
  // The maker's dispatch desk (9 Sep 2026): takes the network's stock orders
  // through to dispatch — the order desk, the store, the dispatch form.
  'DISPATCH',
  // The factory's store keeper (9 Sep 2026): the item list and the counts.
  'FACTORY',
  /*
   * The factory's PDI hand (owner, 25 Sep 2026: "create a new user amit
   * factory and he will also pdi store and vehicle and tranfer vehicle to
   * dispatch store"). Checks what the plant sent and passes it into the
   * dispatch store — and nothing else a vehicle can have done to it. Its own
   * role, not FACTORY widened: the parts keeper was never asked to pass a
   * PDI, and not DISPATCH: dispatching is the office's.
   */
  'FACTORY_PDI',
  /*
   * The distributor's own store desk (15 Sep 2026: "add dispatch desk and
   * rights to vivek as well").
   *
   * A counter that also sends stock on. Bright on Wheels takes vehicles from
   * the maker into its store and has to hand them to its mitrs and outlets;
   * until now only its three administrators could move a vehicle, while
   * fifty-three orders stood open. This is the counter it sits at — it sells
   * from the store like any counter — plus the order desk and the dispatch
   * form. Paired with a scopeParty on the DISTRIBUTOR, which keeps its stock
   * the store's own while its ORDERS are the whole book's.
   */
  'STORE_DESK',
  'SALES_HEAD',
  /*
   * A billing desk (14 Sep 2026). The maker bills the distributor company for
   * what it dispatched; the distributor bills its own outlet or mitr for what
   * they sold. One role, two books, the same job — deliberately NOT
   * ADMINISTRATOR, which carries every permission there is: a billing clerk
   * would inherit roles:manage and could re-grant themselves anything.
   */
  'BILLING',
  /*
   * The billing chain after the bill (owner, 1 Oct 2026: "sandhya uploads bill
   * and sale on no 2 ragini will upload RTO reciept then on no 3 aarti will
   * upload insurance policiy and on no 4 anam check everything that payment
   * has been recieved and on no 5 smriti will check that RC and and no plate
   * has been given then the sale is concluded"). One step each, in both
   * Bright on Wheels and TVS; payment is checked by an administrator.
   */
  'RTO_DESK',
  'INSURANCE_DESK',
  'RC_DESK',
  /*
   * The TVS dispatch hand (owner, 6 Oct 2026: "Create sunil user tvs dispatch
   * guy move the complete dispatch section which already exist to his
   * login"). The TVS dispatch — the store it empties, the form with its plate
   * photos, the day's dispatches — and nothing else; signed in to the TVS book.
   */
  'TVS_DISPATCH',
  /*
   * The telecalling team (owner, 13 Oct 2026: "create 3 sales telecalling team
   * portal their name are neelam, sonali and kanti they are telecalling team
   * they will also fill the enquires in the app"), then, the same evening:
   * "they will not upload enquires they will see inquires which i will upload
   * in excel sheets … like a lead management system". They call the owner's
   * lead sheets (leads.ts) and write what came of each call; they sell, move
   * and order nothing.
   */
  'TELECALLER',
  /*
   * Staff (owner, 14 Oct 2026: "create a new role which will only have
   * attendance and task that i will assign to them thats it", then "add
   * expense as well"). An employee with no book work at all: marks their own
   * attendance, does the tasks the owner gives, adds their expenses. It holds
   * no permission — those three are every employee's by who they are, not by
   * a grant — so it reads no stock, no party, no order and no customer.
   */
  'STAFF',
  /*
   * Service desk (owner, 20 Oct 2026: "i have addes a employee arun he manages
   * all complaints and replacement of the company work on his portal"). Works
   * the complaints and service calls of Bright Autozone, Bright on Wheels and
   * FAB EV — walk-ins too — and fits parts from the factory store against
   * them, the old part going back to the store's faulty bin. Reads the factory
   * store; moves its counts only through a replacement.
   */
  'SERVICE',
  /*
   * Guard (owner, 23 Oct 2026: "all 3 are guard in they will transfer the
   * material from here there"). Sends loader boxes, frames and fitments
   * between the head office store and the Transport Nagar and Darogakheda
   * factories, receives what comes in, receives back what did not arrive,
   * and counts all three places. Reads no vehicle, party, order or customer.
   */
  'GUARD',
  /*
   * Store guard (owner, 23 Oct 2026: "dispatch should be visible to only store
   * guard"). A guard posted at the store gate, where the dispatched vehicles
   * leave: the material register, and the tick on every dispatched vehicle.
   */
  'STORE_GUARD',
  /*
   * Painter (owner, 26 Oct 2026: "add a painter step before team select chasis
   * for motor and controller painter will select chasis and update the paint
   * on the chasis"). Records the colour a production-store chassis is
   * painted; the factory store gives it parts only after.
   */
  'PAINTER',
] as const;

/** The guards — at any gate, or at the store's (23 Oct 2026). */
export function isGuardRole(roleKey: string | null | undefined): boolean {
  return roleKey === 'GUARD' || roleKey === 'STORE_GUARD';
}

/** The staff role: attendance, tasks and expenses, and nothing in any book (14 Oct 2026). */
export function isStaffRole(roleKey: string | null | undefined): boolean {
  return roleKey === 'STAFF';
}

/** The roles that work the billing desk and nothing else — their home is /billing. */
export const BILLING_DESK_ROLES: readonly RoleKey[] = ['BILLING', 'RTO_DESK', 'INSURANCE_DESK', 'RC_DESK'];
export type RoleKey = (typeof ROLE_KEYS)[number];

/** Which statuses count as "still ours" for stock maths. */
export const BRIGHT_STOCK_STATUSES: VehicleStatus[] = ['IN_BRIGHT_STOCK'];

/**
 * Statuses that count as stock — what the business still has to sell.
 *
 * Only the outlet and the mitrs. A vehicle handed to a dealer is gone from this
 * ledger by the same logic as a sale: the dealer owns it, and what they do with
 * it afterwards is their business, not stock this company can move. A sold or
 * returned vehicle is out for the obvious reasons. Everything out of stock
 * stays findable by chassis with its full history — leaving stock is not being
 * erased — but it is not counted, not aged, and not on the stock screens.
 */
export const HELD_STATUSES: VehicleStatus[] = [
  'PENDING_ALLOCATION',
  'IN_BRIGHT_STOCK',
  'AT_COMPANY_OUTLET',
  'AT_SUB_DEALER',
];

/** Out of stock, but still with a named party rather than a customer. */
export const AT_DEALER_STATUSES: VehicleStatus[] = ['AT_DEALER'];

/**
 * Channel a vehicle sits in, derived from custody party type.
 *
 * The vocabulary is Bright Autozone's: the organisation supplies the fleet,
 * Bright on Wheels is its distribution partner, dealers sell, and the second
 * tier is the *mitr* — marked "MITRA" right after the name in the dispatch log.
 * The enum keys are storage and never change; the words are the business's own.
 */
export const CHANNEL_BY_STATUS: Record<VehicleStatus, string> = {
  PENDING_ALLOCATION: 'Pending allocation',
  IN_BRIGHT_STOCK: 'Bright on Wheels',
  AT_COMPANY_OUTLET: 'Company outlet',
  AT_DEALER: 'Dealer',
  AT_SUB_DEALER: 'Mitr',
  SOLD: 'Sold',
  RETURNED: 'Returned to Bright Autozone',
  CANCELLED: 'Cancelled',
};

export const STATUS_LABELS: Record<VehicleStatus, string> = {
  PENDING_ALLOCATION: 'Pending allocation',
  IN_BRIGHT_STOCK: 'With Bright on Wheels',
  AT_COMPANY_OUTLET: 'At company outlet',
  // A dealer holds no stock: a vehicle sent to one is Autozone's sale (owner,
  // 1 Oct 2026: "dealers cant have stock only bright on wheels mitra and
  // outlet will have stock"). The stored status stays AT_DEALER — returns and
  // the dispatched register read it.
  AT_DEALER: 'Sold to dealer',
  AT_SUB_DEALER: 'With mitr',
  SOLD: 'Sold',
  RETURNED: 'Returned to Bright Autozone',
  CANCELLED: 'Cancelled',
};

export const PARTY_TYPE_LABELS: Record<PartyType, string> = {
  MANUFACTURER: 'Bright Autozone',
  DISTRIBUTOR: 'Bright on Wheels',
  COMPANY_OUTLET: 'Company owned outlet',
  DEALER: 'Dealer',
  SUB_DEALER: 'Mitr',
  DIRECT_CUSTOMER: 'Customer',
  WAREHOUSE: 'Warehouse',
};

/**
 * Who places a stock order: the area sales managers (owner, 25 Sep 2026:
 * "disable order placing for everyone except asm some orders are getting
 * double placed"), the head ASM (SALES_HEAD, an ASM over the whole book) —
 * and the store desk, for its own store (owner, 29 Sep 2026: "vivek sales …
 * he need to place an order on behalf of bow for dispatch"). A store desk
 * sign-in sits at the store, so its order is always for the store: the server
 * pins it there whatever the form says. The office, the dispatch and billing
 * desks and the counters read and work orders; they do not place them.
 */
export const ORDER_PLACING_ROLES: readonly RoleKey[] = ['ASM', 'SALES_HEAD', 'STORE_DESK'];

/**
 * The area sales managers, of either rank: an ASM, and a head ASM
 * (SALES_HEAD — Manish, Goswami). Not the same list as who places orders: the
 * store desk places its own store's orders and is no ASM. An ASM reads their
 * own orders and dispatches, never the desk's whole book, and never opens
 * Autozone's own book (owner, 30 Sep 2026: "All dispatches are also visible to
 * manish in his sign in portal").
 */
export const ASM_ROLE_KEYS: readonly RoleKey[] = ['ASM', 'SALES_HEAD'];

/**
 * What an enquiry is for (owner, 1 Oct 2026: "add enquiry form in every asm as
 * well and also an option to mention where enquiry is of dealer/mitra/outlet").
 */
export const ENQUIRY_CHANNELS = ['CUSTOMER', 'DEALER', 'MITRA', 'OUTLET'] as const;
export type EnquiryChannelKey = (typeof ENQUIRY_CHANNELS)[number];
export const ENQUIRY_CHANNEL_LABELS: Record<EnquiryChannelKey, string> = {
  // Somebody buying a vehicle (13 Oct 2026) — most of what the telecalling team hears.
  CUSTOMER: 'Customer',
  DEALER: 'Dealer',
  MITRA: 'Mitra',
  OUTLET: 'Outlet',
};

/**
 * Who an announcement reaches (owner, 1 Oct 2026: "create a anouncemnent
 * section where i will upload flyers and it will start reflecting in
 * mitr/outlet/ dealer"), and which counter is which.
 */
export const ANNOUNCEMENT_AUDIENCES = ['MITRA', 'OUTLET', 'DEALER'] as const;
export type AnnouncementAudience = (typeof ANNOUNCEMENT_AUDIENCES)[number];
export const ANNOUNCEMENT_AUDIENCE_LABELS: Record<AnnouncementAudience, string> = {
  MITRA: 'Mitras',
  OUTLET: 'Outlets',
  DEALER: 'Dealers',
};
export function announcementAudienceOf(partyType: string | null | undefined): AnnouncementAudience | null {
  if (partyType === 'SUB_DEALER') return 'MITRA';
  if (partyType === 'COMPANY_OUTLET') return 'OUTLET';
  if (partyType === 'DEALER') return 'DEALER';
  return null;
}

/**
 * A counter's standing on the network lists (owner, 3 Oct 2026: "on dealers
 * section in the list give a option for active inactive and disabled their on
 * the list only so quickly all list can be fixed"; he chose what each means):
 *
 *   ACTIVE   — trading: stock goes to it, it is in every picker.
 *   INACTIVE — no stock moved to it, out of the pickers (`isActive` false, as
 *              "deactivated" always worked).
 *   DISABLED — all of that, its sign-ins stop, and it leaves the lists unless
 *              they are asked for disabled ones (`disabledAt` set).
 *
 * Stored as the two fields, so everything that already stops at an inactive
 * party stops at a disabled one too.
 */
export const PARTY_STATUSES = ['ACTIVE', 'INACTIVE', 'DISABLED'] as const;
export type PartyStatus = (typeof PARTY_STATUSES)[number];
export const PARTY_STATUS_LABELS: Record<PartyStatus, string> = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
  DISABLED: 'Disabled',
};
/**
 * The head-admin desks: the shared sign-ins behind the "Bright · Head admin"
 * PIN door, one per book (`PORTALS` in the API's auth service holds the same
 * list). They are the owner's doors, not anybody's job. The fifth is Sunny's
 * own, the CEO's desk (26 Oct 2026, `CEO_DESK_EMAIL` in ceo.ts).
 */
export const HEAD_ADMIN_DESK_EMAILS: readonly string[] = [
  'admin@brightautozone.in',
  'admin@brightonwheels.in',
  'admin@fabev.in',
  'tvs@brightonwheels.in',
  'ceo@brightautozone.in',
];

/**
 * Who may undo any sale, at any step, in the book they stand in (owner, 31 Oct 2026: "admin can undo anything"): the
 * owner's desks and every administrator. Never a counter's own sign-in.
 */
export function undoesAnySale(user: { email: string; roleKey?: string | null; role?: { key: string } | null; scopePartyId?: string | null; scopeParty?: unknown } | null | undefined): boolean {
  if (!user || user.scopePartyId || user.scopeParty) return false;
  return isHeadAdminDesk(user) || (user.roleKey ?? user.role?.key) === 'ADMINISTRATOR';
}

/**
 * Who passes a factory chassis's PDI into the dispatch store (owner, 3 Nov 2026: "only brightautozone admin and factory
 * will have this power nobody else"; the same day: "give rahul also power … and vivek also"): the Bright Autozone admin
 * desk, Rahul, Vivek's dispatch desk and the factory's PDI sign-in. Not the other administrators. And Neeraj at the factory
 * gate (owner, 5 Nov 2026: "trasnfer vehicle system from production store to dispatch store should also be in neeraj
 * factory portal").
 */
export const PDI_PASSER_EMAILS: readonly string[] = ['admin@brightautozone.in', 'rahul@brightautozone.in', 'vivek-desk@brightautozone.in', 'neerajfactory@brightautozone.in'];

export function passesPdi(
  user: { email: string; roleKey?: string | null; role?: { key: string } | null } | null | undefined,
  permissions: readonly string[],
): boolean {
  if (!user) return false;
  if (PDI_PASSER_EMAILS.includes(user.email.trim().toLowerCase())) return true;
  // The PDI key, held by a role made for it — the factory's, or a cover of it — never by an administrator, who holds every key.
  return permissions.includes('vehicles:pdi') && (user.roleKey ?? user.role?.key) !== 'ADMINISTRATOR';
}

/** One of the owner's head-admin desks — the doors only the owner uses. */
export function isHeadAdminDesk(user: { email: string } | null | undefined): boolean {
  return Boolean(user) && HEAD_ADMIN_DESK_EMAILS.includes(user!.email.trim().toLowerCase());
}

/**
 * Who takes chassis out of Purchase data (owner, 30 Oct 2026: "some chassis need to be removed from
 * purchase data only abhishek will get this feature"). Abhishek alone — not the other administrators.
 */
export const PURCHASE_DATA_REMOVER_EMAILS: readonly string[] = ['abhishek@brightonwheels.in'];
export function removesPurchaseData(user: { email: string } | null | undefined): boolean {
  return Boolean(user) && PURCHASE_DATA_REMOVER_EMAILS.includes(user!.email.trim().toLowerCase());
}

/**
 * What the person rules read. `seatOfId` is set on another desk of a person
 * (Vivek's dispatch desk, 20 Oct 2026, see desks.ts): every rule built on
 * isVerifiedEmployee — attendance, tasks, chat, expenses, verification —
 * leaves such a desk out, so the person is counted once, on their own sign-in.
 * A caller that lists people must select `seatOfId` and pass it.
 */
export interface PersonRuleInput {
  roleKey: string;
  email: string;
  seatOfId?: string | null;
}

/**
 * Whether a sign-in is an employee who puts their Aadhaar and details on file
 * (6 Oct 2026): anyone but a mitra, outlet or dealer's login (BUYER) — and,
 * since 12 Oct 2026, not a head-admin desk either (owner: "it is showing aadhar
 * verification in admin as well remove that aadhar verification is for
 * employee only"). Staff who hold the Administrator role — Surbhi, Jitendra and
 * the rest — are employees and still verify.
 */
export function isVerifiedEmployee(user: PersonRuleInput): boolean {
  // Another desk of a person (20 Oct 2026) is not a second employee: the person's own sign-in is.
  if (user.seatOfId) return false;
  return user.roleKey !== 'BUYER' && !HEAD_ADMIN_DESK_EMAILS.includes(user.email.trim().toLowerCase());
}

/**
 * Who adds their own expenses for the office to approve (owner, 12 Oct 2026:
 * "Add adding expense option to all the employees"). Every employee, the ASMs
 * from their field desk and the rest from "My expenses" — never a counter's
 * sign-in or a head-admin desk. Drivers have no sign-in since 5 Nov 2026.
 */
export function addsExpenses(user: PersonRuleInput): boolean {
  return isVerifiedEmployee(user);
}

/**
 * Who keeps expenses of their own, to be paid back onto their UPI QR (owner,
 * 4 Nov 2026: "Give adding expense option to head admin and sunny as well he
 * might want to add some expense he did for company and he want to get it
 * reimbursed through surbhi"): every employee who adds them, and the owner's
 * head-admin desks — Sunny's among them. Their claims are filed in their own
 * book and answered by its office, as anyone's are; one's own claim never
 * waits in one's own approvals.
 *
 * The company-expense requests (the office paying a supplier) stay with
 * `addsExpenses`: Sunny's step there answers others' asks over ₹50,000 and
 * refuses his own, so an ask of his would wait for nobody.
 */
export function claimsExpenses(user: PersonRuleInput): boolean {
  if (user.seatOfId) return false;
  return addsExpenses(user) || HEAD_ADMIN_DESK_EMAILS.includes(user.email.trim().toLowerCase());
}

/**
 * The statuses that mean a vehicle is standing in somebody's stock (owner, 1 Oct
 * 2026: "dealers cant have stock only bright on wheels mitra and outlet will
 * have stock"). SOLD, RETURNED, CANCELLED and AT_DEALER are not stock. Every
 * stock count, list and ageing figure reads this one list.
 */
export const HOLDING_STATUSES = ['PENDING_ALLOCATION', 'IN_BRIGHT_STOCK', 'AT_COMPANY_OUTLET', 'AT_SUB_DEALER'] as const;
export function isHoldingStatus(status: string | null | undefined): boolean {
  return (HOLDING_STATUSES as readonly string[]).includes(status ?? '');
}

/** What a sign-in at a disabled counter is told, at sign-in and on every request. */
export const COUNTER_DISABLED = 'Your counter has been switched off. Ask the office to switch it back on.';

export function partyStatusOf(party: { isActive: boolean; disabledAt?: Date | string | null }): PartyStatus {
  if (party.disabledAt) return 'DISABLED';
  return party.isActive ? 'ACTIVE' : 'INACTIVE';
}

/** Whether a sign-in of this role is an ASM of either rank. */
export function isAsmRole(roleKey: string | null | undefined): boolean {
  return (ASM_ROLE_KEYS as readonly string[]).includes(roleKey ?? '');
}

/**
 * Whether a sign-in of this role may place a new stock order: the order-placing
 * roles, and the administrators, for any counter (owner, 1 Oct 2026: "Give
 * order Placing rights to admin as well"). The administrators are not in
 * ORDER_PLACING_ROLES: that list also says who answers an order for their own
 * counters, and an administrator answers for none — their order is placed
 * straight, the way Jitendra's (`ordersForAll`) is.
 */
export function placesOrders(roleKey: string | null | undefined): boolean {
  return (ORDER_PLACING_ROLES as readonly string[]).includes(roleKey ?? '') || roleKey === 'ADMINISTRATOR';
}

/**
 * Whether a sign-in places orders for the company outlets and nothing else:
 * the outlets right (Surbhi, 30 Sep 2026) on a sign-in that is no ASM, head
 * ASM or store desk and lacks the any-counter right. An administrator with it
 * stays outlets-only — every administrator ordering (1 Oct 2026) did not widen
 * the one grant the owner gave a shape.
 */
export function placesOutletOrdersOnly(
  roleKey: string | null | undefined,
  rights: { ordersForOutlets?: boolean; ordersForAll?: boolean },
): boolean {
  return !(ORDER_PLACING_ROLES as readonly string[]).includes(roleKey ?? '') && !rights.ordersForAll && Boolean(rights.ordersForOutlets);
}

/**
 * The counters that order for themselves again, through their ASM (owner,
 * 3 Oct 2026: "now everybody can place orders from their app dealers outlet
 * and mitr and the orders will go to their respected asm for approval"). From
 * 25 Sep to 3 Oct only an ASM placed orders, because orders were going in
 * twice; now a counter's order waits for its own ASM — Accept, Already
 * placed, or Payment pending — and only an accepted one reaches the desk.
 *
 * A counter changes or deletes only the orders it raised, and only while its
 * ASM has not accepted them: once on the desk, the order is the ASM's and the
 * office's. The store desk sits at a DISTRIBUTOR and is not one of these: it
 * orders for its store as an order-placing role.
 */
export const ORDERING_COUNTERS: readonly PartyType[] = ['SUB_DEALER', 'COMPANY_OUTLET', 'DEALER'];

/** Whether a sign-in seated at this party type is a counter that orders through its ASM. */
export function isOrderingCounter(seatType: string | null | undefined): boolean {
  return (ORDERING_COUNTERS as readonly string[]).includes(seatType ?? '');
}

/** Status a vehicle takes when a party of this type holds it. */
export const STATUS_FOR_PARTY_TYPE: Record<PartyType, VehicleStatus> = {
  MANUFACTURER: 'PENDING_ALLOCATION',
  DISTRIBUTOR: 'IN_BRIGHT_STOCK',
  WAREHOUSE: 'IN_BRIGHT_STOCK',
  COMPANY_OUTLET: 'AT_COMPANY_OUTLET',
  DEALER: 'AT_DEALER',
  SUB_DEALER: 'AT_SUB_DEALER',
  DIRECT_CUSTOMER: 'SOLD',
};

/**
 * The books the product keeps, by their stable ids. Ids never change; names
 * and nouns do, and live in `ORG_NOUNS` below.
 */
export const ORG_IDS = {
  wholesale: 'org_bright_autozone_wholesale',
  bow: 'org_bright_on_wheels',
  fabev: 'org_fab_ev',
  /** Bright on Wheels' TVS business — its own book, fed by TVS Motor's vouchers (8 Sep 2026). */
  tvs: 'org_bow_tvs',
  /**
   * The practice book (15 Sep 2026: "Create a demo id with sale form so we can
   * use it for educating the mitra and outlet").
   *
   * A book of its own so a trainee can fill the sale form, press Sell and get
   * it wrong, without any of it reaching real stock, the billing desk, or the
   * owner's sheet. Nothing is ever dispatched into it and nothing syncs out of
   * it; it holds practice vehicles a trainer resets between sittings.
   */
  demo: 'org_demo_training',
} as const;

/**
 * The books that run on BUSY uploads — purchases and sales come in from BUSY,
 * and the app is the CRM after that (owner, 19 Oct 2026: "think of it as SAAS
 * any company will upload their purchase and sale from busy and our app will
 * provide end to end crm after that bright autozone and bright on wheels are
 * differnt rest all company are same fab ev and tvs").
 */
export const BUSY_BOOKS: readonly string[] = [ORG_IDS.tvs, ORG_IDS.fabev];

/**
 * The books that keep a counter ledger (owner, 30 Oct 2026: FAB EV gets "a proper billing system like
 * bright on wheels", its own ledger among it). Each reads only its own sales; the maker's book reads
 * Bright on Wheels', as it always has.
 */
export const LEDGER_BOOKS: readonly string[] = [ORG_IDS.bow, ORG_IDS.fabev];

/**
 * What the office's sale on the bill photo records where a box was left blank (owner, 26–27 Oct
 * 2026). Only the bill is asked for — "they can continue just by adding bill photo and they can
 * fill other details afterwards as well such as bill number date and amount" — so a blank buyer
 * reads as the bill's and a blank reason as the lane's own, until the office puts them in.
 * (On 26 Oct FAB EV and Bright on Wheels had the photo alone and nothing else; the form came back
 * the next day, with nothing on it insisted on but the bill.)
 */
export const BILL_PHOTO_BUYER = 'As on the bill photo';
export const BILL_PHOTO_REASON = 'Sold on the bill photo alone — the bill holds the buyer, the number and the amount.';

/** A BUSY book's sales from this IST day on are on its billing desk, billed in BUSY (owner, 19 Oct 2026: "Sales from 1 Oct 2026"). */
export const BUSY_DESK_FROM = '2026-10-01';

/**
 * The words a book uses for itself. The retail app was written for one
 * distributor and said "Bright on Wheels" in its labels; the FAB EV book
 * (7 Sep 2026) is the same product with another name in those places. Every
 * label that names the distributor goes through here, keyed by organisation
 * id or slug, so a new partner is one entry, not a rename across the app.
 */
export interface OrgNouns {
  /** The distributor, as a proper noun: "Bright on Wheels", "FAB EV". */
  distributor: string;
  /** Its store, in a sentence: "the Bright on Wheels store". */
  store: string;
  /** The short brand on mastheads and portal cards. */
  brand: string;
  /** True for the OEM's wholesale book, which speaks sales rather than stock. */
  wholesale: boolean;
}

const BOW_NOUNS: OrgNouns = {
  distributor: 'Bright on Wheels',
  store: 'the Bright on Wheels store',
  brand: 'Bright on Wheels',
  wholesale: false,
};
const FABEV_NOUNS: OrgNouns = {
  distributor: 'FAB EV',
  store: 'the FAB EV store',
  brand: 'FAB EV',
  wholesale: false,
};
const AUTOZONE_NOUNS: OrgNouns = {
  distributor: 'Bright Autozone',
  store: "Bright Autozone's stock",
  brand: 'Bright Autozone',
  wholesale: true,
};
const TVS_NOUNS: OrgNouns = {
  distributor: 'Bright on Wheels',
  store: 'the TVS store',
  brand: 'Bright on Wheels · TVS',
  wholesale: false,
};

/**
 * Books that are one company's two lines (8 Sep 2026): Bright on Wheels sells
 * Autozone's e-rickshaws and TVS's autos through the same mitrs and outlets.
 * A session in one book may read and act in the other, as its linked party.
 */
export const BOOK_LINKS: Record<string, string> = {
  [ORG_IDS.bow]: ORG_IDS.tvs,
  [ORG_IDS.tvs]: ORG_IDS.bow,
};

/**
 * The books a session may open from its own, besides its own. Bright on
 * Wheels opens its TVS line — its ASMs and administrators run both — but a
 * TVS session stays in TVS: its staff are TVS-only (owner, 8 Sep 2026:
 * "Sandhya and Anam will only have access to the TVS book"). Reads about
 * the twin (BOOK_LINKS) stay two-way; only the switch is one-way.
 */
export const BOOK_OPENS: Record<string, string[]> = {
  [ORG_IDS.bow]: [ORG_IDS.tvs],
};

/**
 * The books by the names the owner uses (29 Sep 2026). The organisation rows
 * carry older names — Bright on Wheels' is still "Bright Autozone" in the
 * table, and the maker's "Bright Autozone Wholesale" — so a screen that names
 * a book says it from here.
 */
export const BOOK_NAMES: Readonly<Record<string, string>> = {
  [ORG_IDS.wholesale]: 'Bright Autozone',
  [ORG_IDS.bow]: 'Bright on Wheels',
  [ORG_IDS.tvs]: 'TVS',
  [ORG_IDS.fabev]: 'FAB EV',
};

/** A book's name as the owner says it, else the name its row carries. */
export function bookNameOf(organizationId: string, stored: string): string {
  return BOOK_NAMES[organizationId] ?? stored;
}

/** Whether a session in `fromOrganizationId` may open the book `target`. */
export function canOpenBook(fromOrganizationId: string, target: string): boolean {
  return (BOOK_OPENS[fromOrganizationId] ?? []).includes(target);
}

/** A book and its twin, for reads that span the pair — else the book alone. */
export function bookAndTwin(organizationId: string): string[] {
  const twin = BOOK_LINKS[organizationId];
  return twin ? [organizationId, twin] : [organizationId];
}

/**
 * The books whose area sales managers a book's parties may be assigned to.
 *
 * Dealers are Autozone's own (owner, 18 Sep 2026: "bow doesn't have any
 * dealers — dealers are of autozone, who buy directly from autozone"), but the
 * people who look after them are Bright on Wheels' ASMs — Manish and Goswami
 * take the dealers' orders. So the maker's book reads the distributor's roster
 * as its own, the way a TVS mitr is handled by its Bright on Wheels twin's ASM.
 */
export function asmRosterBooks(organizationId: string): string[] {
  if (organizationId === ORG_IDS.wholesale) return [ORG_IDS.wholesale, ORG_IDS.bow];
  return bookAndTwin(organizationId);
}

/**
 * Every book whose counters an ASM of `asmOrganizationId` may be assigned —
 * `asmRosterBooks` read the other way round (13 Oct 2026, for the ASM's sales
 * performance). A Bright on Wheels ASM looks after Autozone's dealers, Bright
 * on Wheels' mitrs and outlets, and their TVS twins; a FAB EV ASM, FAB EV's.
 */
export function asmServedBooks(asmOrganizationId: string): string[] {
  return Object.values(ORG_IDS).filter((book) => asmRosterBooks(book).includes(asmOrganizationId));
}

/**
 * The short key a client sends to switch books. A linked pair (bow ↔ tvs)
 * is open to any session in either; the rest only to a head-office login
 * that may open every book (`User.allBooks`, 8 Sep 2026).
 */
export const BOOK_KEYS: Record<string, string> = {
  autozone: ORG_IDS.wholesale,
  bow: ORG_IDS.bow,
  tvs: ORG_IDS.tvs,
  fabev: ORG_IDS.fabev,
};

export const ORG_NOUNS: Record<string, OrgNouns> = {
  [ORG_IDS.bow]: BOW_NOUNS,
  'bright-on-wheels': BOW_NOUNS,
  [ORG_IDS.fabev]: FABEV_NOUNS,
  'fab-ev': FABEV_NOUNS,
  [ORG_IDS.tvs]: TVS_NOUNS,
  'bow-tvs': TVS_NOUNS,
  [ORG_IDS.wholesale]: AUTOZONE_NOUNS,
  'bright-autozone-wholesale': AUTOZONE_NOUNS,
};

/**
 * The nouns for a book, by id or slug. An unknown book (a test fixture, a
 * future partner not yet listed) is described by its own name.
 */
export function orgNouns(orgIdOrSlug: string | null | undefined, fallbackName?: string | null): OrgNouns {
  if (orgIdOrSlug && ORG_NOUNS[orgIdOrSlug]) return ORG_NOUNS[orgIdOrSlug]!;
  const name = (fallbackName ?? '').trim() || 'Bright on Wheels';
  return { distributor: name, store: `the ${name} store`, brand: name, wholesale: false };
}

/** `STATUS_LABELS`, said in a book's own words. */
export function statusLabelFor(status: VehicleStatus, nouns: OrgNouns): string {
  if (status === 'IN_BRIGHT_STOCK') return `With ${nouns.distributor}`;
  return STATUS_LABELS[status];
}

/** `CHANNEL_BY_STATUS`, said in a book's own words. */
export function channelLabelFor(status: VehicleStatus, nouns: OrgNouns): string {
  if (status === 'IN_BRIGHT_STOCK') return nouns.distributor;
  return CHANNEL_BY_STATUS[status];
}

/** `PARTY_TYPE_LABELS`, said in a book's own words. */
export function partyTypeLabelFor(type: PartyType, nouns: OrgNouns): string {
  if (type === 'DISTRIBUTOR') return nouns.distributor;
  return PARTY_TYPE_LABELS[type];
}

export const zPartyType = z.enum(PARTY_TYPES);
export const zVehicleStatus = z.enum(VEHICLE_STATUSES);
export const zAlertKind = z.enum(ALERT_KINDS);
export const zAlertStatus = z.enum(ALERT_STATUSES);
export const zRoleKey = z.enum(ROLE_KEYS);
